import { useRender } from '@base-ui/react/use-render'
import { createContext, use, useEffect, useId, useState } from 'react'

import { cn } from '../../../lib/utils.ts'
import { Heading } from '../heading/heading.tsx'
import { Text } from '../text/text.tsx'

const SectionContext = createContext<((titleId: string | undefined) => void) | null>(null)

/**
 * `Section` is a titled content block: `Section.Root` + `Section.Header`
 * (`Section.Title` + optional `Section.Subtitle` and `Section.Actions`) + `Section.Body`. A layout
 * primitive like `Page`/`Item` — slots, no config props.
 *
 * `Section.Title` registers its id so `Section.Root` exposes the `<section>` as
 * an `aria-labelledby` landmark named by its title (and stays unlabelled when
 * there is no title).
 *
 * @summary Composable titled section (header / title / actions / body).
 */
function SectionRoot(props: SectionRoot.Props) {
  const { className, ...rest } = props
  const [titleId, setTitleId] = useState<string>()
  return (
    <SectionContext value={setTitleId}>
      <section
        data-slot="section-root"
        aria-labelledby={titleId}
        className={cn('flex flex-col gap-stack', className)}
        {...rest}
      />
    </SectionContext>
  )
}
namespace SectionRoot {
  export type Props = React.ComponentProps<'section'>
}

function SectionHeader(props: SectionHeader.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="section-header"
      // Two columns so the title and its subtitle stack on the left while actions stay right.
      // `row-span-full` on the actions spans only the rows that exist, so a header without a
      // subtitle renders exactly as it did when this was a flex row.
      className={cn('grid auto-rows-min grid-cols-[1fr_auto] items-center gap-x-stack gap-y-20', className)}
      {...rest}
    />
  )
}
namespace SectionHeader {
  export type Props = React.ComponentProps<'div'>
}

function SectionTitle(props: SectionTitle.Props) {
  const { className, children, id, ...rest } = props
  const generatedId = useId()
  const titleId = id ?? generatedId
  const registerTitle = use(SectionContext)
  useEffect(
    function registerTitleId() {
      registerTitle?.(titleId)
      return function unregisterTitleId() {
        registerTitle?.(undefined)
      }
    },
    [registerTitle, titleId],
  )
  return (
    <Heading
      data-slot="section-title"
      id={titleId}
      variant="h6"
      level={2}
      className={cn('col-start-1', className)}
      {...rest}
    >
      {children}
    </Heading>
  )
}
namespace SectionTitle {
  export type Props = useRender.ComponentProps<'h2'>
}

function SectionSubtitle(props: SectionSubtitle.Props) {
  const { className, ...rest } = props
  return (
    <Text
      variant="body2"
      render={<div />}
      data-slot="section-subtitle"
      className={cn('col-start-1 text-content-base-basic', className)}
      {...rest}
    />
  )
}
namespace SectionSubtitle {
  export type Props = React.ComponentProps<'div'>
}

function SectionActions(props: SectionActions.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="section-actions"
      className={cn('col-start-2 row-span-full row-start-1 flex shrink-0 items-center gap-inline', className)}
      {...rest}
    />
  )
}
namespace SectionActions {
  export type Props = React.ComponentProps<'div'>
}

function SectionBody(props: SectionBody.Props) {
  const { className, ...rest } = props
  return <div data-slot="section-body" className={cn('flex flex-col gap-stack', className)} {...rest} />
}
namespace SectionBody {
  export type Props = React.ComponentProps<'div'>
}

const Section = {
  Root: SectionRoot,
  Header: SectionHeader,
  Title: SectionTitle,
  Subtitle: SectionSubtitle,
  Actions: SectionActions,
  Body: SectionBody,
}

namespace Section {
  export namespace Root {
    export type Props = SectionRoot.Props
  }
  export namespace Header {
    export type Props = SectionHeader.Props
  }
  export namespace Title {
    export type Props = SectionTitle.Props
  }
  export namespace Subtitle {
    export type Props = SectionSubtitle.Props
  }
  export namespace Actions {
    export type Props = SectionActions.Props
  }
  export namespace Body {
    export type Props = SectionBody.Props
  }
}

export { Section }
